// Send window (V0.4.8): shows the email exactly as it will go — recipients (add / remove, Cc), subject, note, the
// attachment — and only sends when a person ticks "recipients checked" and presses Send. Used for client finance
// (certificate pack) and for PBS (review comments).
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Button, Drawer, errText, useToast } from '../kit';
import { C3Loader, C3Busy } from '../C3Loader';
import { OfflineNote, useConnection } from '../../lib/connection';

type Preview = { to: string[]; cc: string[]; defaults: string[]; suggest?: { email: string; name: string; org: string }[]; subject: string; html: string; text: string; attachment?: { filename: string; bytes: number } | null };
const EMAIL_RE = /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/;
const kb = (n: number) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function Recipients({ label, list, onChange }: { label: string; list: string[]; onChange: (l: string[]) => void }) {
  const [txt, setTxt] = useState('');
  const add = () => {
    const parts = txt.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return;
    const bad = parts.filter((p) => !EMAIL_RE.test(p));
    if (bad.length) return;
    onChange([...new Set([...list, ...parts])]);
    setTxt('');
  };
  const bad = txt.trim() && txt.split(/[\s,;]+/).filter(Boolean).some((p) => !EMAIL_RE.test(p.trim()));
  return (
    <div className="sw-rcpt">
      <span className="sw-k">{label}</span>
      <div className="sw-chips">
        {list.map((e) => (
          <span key={e} className="sw-chip">
            {e}
            <button type="button" aria-label={`Remove ${e}`} onClick={() => onChange(list.filter((x) => x !== e))}>
              ×
            </button>
          </span>
        ))}
        <input
          value={txt}
          aria-label={`Add ${label} address`}
          placeholder={list.length ? 'add another…' : 'name@company.com'}
          className={bad ? 'bad' : ''}
          onChange={(e) => setTxt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
              e.preventDefault();
              add();
            }
          }}
          onBlur={add}
        />
      </div>
    </div>
  );
}

export function SendWindow({
  title,
  body,
  defaultNote = '',
  onClose,
  onSent,
  extras,
  endpoint = 'claim-action',
}: {
  /** V0.4.15: the function that previews and sends (weekly reports use weekly-action) */
  endpoint?: string;
  /** client finance (V0.4.10): ready-to-proceed badge + what else goes in the email */
  extras?: { rtp: '' | 'yes' | 'hold' | 'no'; swiApproved: boolean };
  title: string;
  /** the claim-action request without note / to / cc / preview, e.g. {action:'send_finance', item_id, include} */
  body: Record<string, unknown>;
  defaultNote?: string;
  onClose: () => void;
  onSent: (to: string[], o?: { markSwiApproved: boolean }) => void;
}) {
  const toast = useToast();
  const conn = useConnection();
  const [p, setP] = useState<Preview | null>(null);
  const [err, setErr] = useState('');
  const [to, setTo] = useState<string[] | null>(null);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [note, setNote] = useState(defaultNote);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState<'preview' | 'send' | null>('preview');
  const first = useRef(true);
  const [x, setX] = useState({ rtp: extras?.rtp ?? '', topic: true, claim_no: true, building: true, link: true });
  const [markSwi, setMarkSwi] = useState(true);
  const xBody = extras ? { extras: x } : {};

  async function load(n: string) {
    setBusy('preview');
    setErr('');
    try {
      const r = await api<Preview>(endpoint, { ...body, ...xBody, note: n, preview: true });
      setP(r);
      if (first.current) {
        setTo(r.to);
        setSubject(r.subject);
        first.current = false;
      }
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(null);
    }
  }
  useEffect(() => void load(note), []); // eslint-disable-line react-hooks/exhaustive-deps
  // refresh the preview when the note changes (after a pause in typing)
  useEffect(() => {
    if (first.current) return;
    const t = setTimeout(() => void load(note), 700);
    return () => clearTimeout(t);
  }, [note, x]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setChecked(false), [to, cc]);

  async function send() {
    if (!to?.length) return toast({ tone: 'error', text: 'Add at least one recipient' });
    setBusy('send');
    try {
      const r = await api<{ to: string[] }>(endpoint, { ...body, ...xBody, note, to, cc, subject });
      toast({ tone: 'ok', text: `Sent to ${r.to.join(', ')}` });
      onSent(r.to, { markSwiApproved: Boolean(extras && x.rtp === 'yes' && !extras.swiApproved && markSwi) });
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  }

  const n = (to?.length ?? 0) + cc.length;
  return (
    <Drawer open wide title={title} onClose={onClose}>
      <div className="sw">
        <div className="sw-form">
          {err ? <div className="error-box">{err}</div> : null}
          <Recipients label="To" list={to ?? []} onChange={setTo} />
          {p && p.defaults.length && to && p.defaults.some((d) => !to.includes(d)) ? (
            <button type="button" className="linkbtn small" onClick={() => setTo([...new Set([...(to ?? []), ...p.defaults])])}>
              + add back the site's contacts ({p.defaults.join(', ')})
            </button>
          ) : null}
          {p?.suggest?.some((sg) => !(to ?? []).includes(sg.email)) ? (
            <div className="sw-suggest">
              <span className="muted small">Finance contacts:</span>
              {p.suggest
                .filter((sg) => !(to ?? []).includes(sg.email))
                .map((sg) => (
                  <button key={sg.email} type="button" className="chip" onClick={() => setTo([...(to ?? []), sg.email])} title={sg.email}>
                    + {sg.name || sg.email}
                    {sg.org ? <span className="muted"> · {sg.org}</span> : null}
                  </button>
                ))}
            </div>
          ) : null}
          {p && !p.defaults.length ? <p className="muted small">No contacts with this role on the site yet — type the addresses, or add them under Organisations &amp; contacts → site roles.</p> : null}
          <Recipients label="Cc" list={cc} onChange={setCc} />
          <label className="field">
            <span className="field-label">Subject</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Subject" />
          </label>
          <label className="field">
            <span className="field-label">Note in the email (optional)</span>
            <textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note" />
          </label>
          {extras ? (
            <div className="sw-extras">
              <span className="sw-k">Include in the email</span>
              <div className="sw-rtp" role="radiogroup" aria-label="Ready to proceed">
                {(
                  [
                    ['yes', '✓ Ready to proceed'],
                    ['hold', 'On hold'],
                    ['no', 'Not ready'],
                    ['', 'Don’t show'],
                  ] as const
                ).map(([k, l]) => (
                  <button key={k || 'none'} type="button" role="radio" aria-checked={x.rtp === k} className={`rtp rtp-${k || 'none'} ${x.rtp === k ? 'on' : ''}`} onClick={() => setX({ ...x, rtp: k })}>
                    {l}
                  </button>
                ))}
              </div>
              {x.rtp === 'yes' && !extras.swiApproved ? (
                <label className="check small">
                  <input type="checkbox" checked={markSwi} onChange={(e) => setMarkSwi(e.target.checked)} /> Also mark the claim SWI approved (Ready to proceed) when sent
                </label>
              ) : null}
              <div className="sw-incl">
                {(
                  [
                    ['topic', 'Topic'],
                    ['claim_no', 'Claim #'],
                    ['building', 'Building'],
                    ['link', 'Link to this claim'],
                  ] as const
                ).map(([k, l]) => (
                  <label key={k} className="check small">
                    <input type="checkbox" checked={x[k]} onChange={(e) => setX({ ...x, [k]: e.target.checked })} /> {l}
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          {p?.attachment ? (
            <div className="sw-att">
              <span className="ext ft-pdf">PDF</span>
              <span className="grow">{p.attachment.filename}</span>
              <span className="muted small">{kb(p.attachment.bytes)}</span>
            </div>
          ) : null}
          <label className="check sw-confirm">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} /> I have checked the recipients ({n})
          </label>
          <div className="resp-row">
            <span className="grow" />
            <Button onClick={onClose}>Cancel</Button>
            <OfflineNote />
            <Button kind="primary" disabled={!checked || !to?.length || Boolean(busy) || conn.status === 'offline'} onClick={() => void send()}>
              {busy === 'send' ? <C3Busy text="Sending…" accent /> : `Send to ${n} recipient${n === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
        <div className="sw-preview">
          <div className="sw-k">Preview {busy === 'preview' ? '· updating…' : ''}</div>
          {p ? (
            <>
              <div className="sw-meta">
                <b>{subject || p.subject}</b>
                <span className="muted small">to {(to ?? []).join(', ') || '—'}{cc.length ? ` · cc ${cc.join(', ')}` : ''}</span>
              </div>
              <iframe title="Email preview" className="sw-frame" sandbox="" srcDoc={p.html} />
            </>
          ) : (
            busy ? <p className="muted c3-inline"><C3Loader size={32} /> Building the email and its attachment…</p> : null
          )}
        </div>
      </div>
    </Drawer>
  );
}
